import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Sparkles, CheckCircle2, ArrowLeft } from 'lucide-react';
import { requestMagicLink } from '@/lib/api';
import { humanAuthError } from '@/lib/authError';
import { AuthLayout } from '@/components/AuthLayout';
// Shared form classes (.form, .field, .label, .input, .submitBtn,
// .error, .serverError, .formSuccess, .btnSecondary). Extracted
// from LoginPage.module.css to AuthForm.module.css in AAP.3 once
// N reached 3 consumers (LoginPage + MagicLinkPage +
// ForgotPasswordPage — then +ResetPasswordPage made it 4).
// Previously imported directly from LoginPage's module; the
// extraction breaks that tight coupling.
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkPage.module.css';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * Admin magic-link REQUEST form (AAP.2). Sibling of the PWA's
 * MagicLinkPage at apps/web/src/pages/auth/MagicLinkPage.tsx but
 * uses admin's AuthLayout + form styling.
 *
 * Submit path: requestMagicLink(email) → POST /api/auth/magic-link
 * with { email, origin: 'admin' }. The origin flag instructs
 * AAP.1's caller-determined host routing to template the email's
 * sign-in link against ADMIN_ORIGIN.
 *
 * After submit, the form is replaced by a success state with the
 * deliberately-ambiguous "if that email is registered..." copy —
 * the request endpoint always returns success regardless of
 * whether the address matches a real user, so the UI must not
 * leak existence either.
 */
export function MagicLinkPage({ onNavigate }: Props) {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    try {
      await requestMagicLink(values.email);
      setSent(true);
    } catch (err) {
      // Same treatment as the sign-in form: branded, status-mapped copy, never
      // the raw transport error.
      setServerError(humanAuthError(err, "Couldn't send the link. Please try again."));
    }
  };

  return (
    <AuthLayout
      title="Magic link sign in"
      subtitle="No password needed. We'll email you a link."
      footer={
        <a
          href="/login"
          onClick={(e) => {
            e.preventDefault();
            onNavigate('/login');
          }}
        >
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Back to sign in
        </a>
      }
    >
      {sent ? (
        <div className={styles.successCard}>
          <CheckCircle2 size={18} className={styles.successIcon} />
          <span>
            If that email is registered, a sign-in link has been sent.
            Check your inbox. The link expires in 15 minutes.
          </span>
        </div>
      ) : (
        <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          {serverError && (
            <div className={formStyles.serverError} role="alert">
              {serverError}
            </div>
          )}
          <div className={formStyles.field}>
            <label htmlFor="email" className={formStyles.label}>
              <Mail size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="admin@evinitiative.com"
              className={`${formStyles.input} ${errors.email ? formStyles.inputError : ''}`}
              {...register('email')}
            />
            {errors.email && (
              <p className={formStyles.error}>{errors.email.message}</p>
            )}
          </div>
          <button
            type="submit"
            className={formStyles.submitBtn}
            disabled={isSubmitting}
          >
            <Sparkles size={14} aria-hidden style={{ marginRight: 6 }} />
            {isSubmitting ? 'Sending…' : 'Email me a link'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
